/**
 * Utility function to find blogs related to a service page
 * Uses the same scoring algorithm as BlogListing page
 * 
 * @param {Array} allBlogs - Array of all blog objects
 * @param {Object} serviceConfig - Configuration object with:
 *   - servicePath: The path of the service (e.g., '/services/web-development')
 *   - serviceTags: Array of tags related to the service (e.g., ['Web Development', 'React', 'Next.js'])
 *   - serviceName: Name of the service (optional, for better matching)
 * @param {number} limit - Maximum number of blogs to return (default: 6)
 * @returns {Array} Array of related blog objects
 */
export const findRelatedBlogs = (allBlogs, serviceConfig, limit = 6) => {
  if (!allBlogs || allBlogs.length === 0) {
    return [];
  }

  const { servicePath, serviceTags = [], serviceName = '' } = serviceConfig;

  // Normalize service path for matching (remove trailing slashes, handle variations)
  const normalizedServicePath = servicePath?.replace(/\/$/, '').toLowerCase();

  // Score articles based on relevance
  const scoredArticles = allBlogs
    .map(blog => {
      let score = 0;

      // Check if blog has internal links pointing to this service
      if (blog.internalLinks && Array.isArray(blog.internalLinks)) {
        const matchingLinks = blog.internalLinks.filter(link => {
          if (!link.url) return false;
          const linkPath = link.url.toLowerCase();
          // Check if link points to this service page
          return linkPath.includes(normalizedServicePath) || 
                 linkPath.includes(servicePath?.toLowerCase());
        });
        if (matchingLinks.length > 0) {
          score += 20; // High weight for direct service links
        }
      }

      // Calculate tag overlap score (higher weight)
      const blogTags = blog.tags || [];
      const commonTags = serviceTags.filter(tag => 
        blogTags.some(blogTag => 
          blogTag.toLowerCase() === tag.toLowerCase() ||
          blogTag.toLowerCase().includes(tag.toLowerCase()) ||
          tag.toLowerCase().includes(blogTag.toLowerCase())
        )
      );
      score += commonTags.length * 10; // 10 points per matching tag

      // Check if service name appears in blog title or description
      if (serviceName) {
        const titleMatch = blog.title?.toLowerCase().includes(serviceName.toLowerCase());
        const descMatch = blog.shortDescription?.toLowerCase().includes(serviceName.toLowerCase());
        if (titleMatch) score += 5;
        if (descMatch) score += 3;
      }

      // Check if any service tag appears in blog title or description
      serviceTags.forEach(tag => {
        const tagLower = tag.toLowerCase();
        if (blog.title?.toLowerCase().includes(tagLower)) score += 3;
        if (blog.shortDescription?.toLowerCase().includes(tagLower)) score += 2;
      });

      return { blog, score, commonTags };
    })
    .filter(item => item.score > 0) // Only include articles with some relevance
    .sort((a, b) => b.score - a.score) // Sort by score descending
    .slice(0, limit); // Get top N articles

  return scoredArticles.map(item => item.blog);
};

/**
 * Format date for display
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
};

